/* PDF export, ported from the prototype's buildPDF(): A4, 56pt margins, KBL lime header
 * band and the confidential footer on every page. Used for conversation export. */

import { jsPDF } from 'jspdf'

export interface PdfBlock {
  h: string
  t: string
}

export function buildPDF(title: string, meta: string[], blocks: PdfBlock[], filename: string) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' })
  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()
  const margin = 56
  const usable = pageW - margin * 2
  let y = 0

  function header() {
    doc.setFillColor(181, 211, 52)
    doc.rect(0, 0, pageW, 64, 'F')
    doc.setTextColor(0, 0, 0)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(13)
    doc.text('KIRLOSKAR BROTHERS LIMITED', margin, 28)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8.5)
    doc.text('Internal AI Assistant · Established 1888 · A Kirloskar Group Company', margin, 44)
    y = 92
  }

  function footer() {
    doc.setFontSize(7.5)
    doc.setTextColor(120, 120, 120)
    doc.text(
      'CONFIDENTIAL - for internal KBL use only. Helpline 1800-123-4443 | marketing@kbl.co.in',
      margin,
      pageH - 34,
    )
    doc.text('(c) 2026 Kirloskar Brothers Limited (India). All Rights Reserved.', margin, pageH - 22)
    doc.setTextColor(0, 0, 0)
  }

  function room(needed: number) {
    if (y + needed > pageH - 56) {
      footer()
      doc.addPage()
      header()
    }
  }

  header()

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(15)
  const titleLines = doc.splitTextToSize(title, usable) as string[]
  doc.text(titleLines, margin, y)
  y += titleLines.length * 18 + 4

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.setTextColor(90, 90, 90)
  for (const line of meta) {
    room(14)
    doc.text(line, margin, y)
    y += 13
  }
  doc.setTextColor(0, 0, 0)
  y += 10

  for (const block of blocks) {
    room(40)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(10)
    const headLines = doc.splitTextToSize(block.h, usable) as string[]
    doc.text(headLines, margin, y)
    y += headLines.length * 13 + 3

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9.5)
    const bodyLines = doc.splitTextToSize(block.t, usable) as string[]
    for (const line of bodyLines) {
      room(13)
      doc.text(line, margin, y)
      y += 12
    }
    y += 12
  }

  footer()
  doc.save(filename)
  return true
}

export function slug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
}

/* -------------------------------------------------- conversation export hook
 * The prototype exposes "Export conversation" from the user menu, which lives in the
 * shell — a lightweight registry lets ChatScreen hand the shell its current transcript. */

export interface ConversationExport {
  title: string
  blocks: PdfBlock[]
}

let conversationGetter: (() => ConversationExport | null) | null = null

export function registerConversationExport(getter: (() => ConversationExport | null) | null) {
  conversationGetter = getter
}

export function currentConversationExport(): ConversationExport | null {
  return conversationGetter ? conversationGetter() : null
}

/* ---------------------------------------------------------------- CSV export */

export function exportCSV(filename: string, rows: (string | number | null | undefined)[][]) {
  const text = rows
    .map((row) =>
      row
        .map((cell) => {
          const v = cell === null || cell === undefined ? '' : String(cell)
          return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v
        })
        .join(','),
    )
    .join('\r\n')
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  a.click()
  URL.revokeObjectURL(a.href)
}
